import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { QueryObserverResult, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconRedirect } from "../../../components/icons";
import { baseAtom, selectInterview } from "../../../stores/jotai/base";
import { zoomIntegrationStateAtom } from "../../../stores/jotai/calender&Zoom";
import {
  TMeetingResponse,
  ZoomRecordingStatus
} from "../../../tanstackQuery/social-integrations/types";
import { GlobalTable } from "./GlobalTable";
import { getDateFormat } from "../../../util";
import { STATUS_ICON_MAP, STATUS_TEXT_MAP } from "./types";
import { RECORDING_INDICATOR_STYLES } from "../../../components/discovery/editor/RecordPlayer";
import { IconMeetingList } from "../../../components/icons/IconMeetingList";

export function MeetingListAction({ meeting }: { meeting: TMeetingResponse }) {
  const theme = useTheme();
  const status = meeting?.recordingStatus;
  const interviewId = meeting?.appInterviewID;
  const [base, setBase] = useAtom(baseAtom);
  const navigate = useNavigate();
  const [redirectionHover, setRedirectionHover] = useState(false);

  const client = useQueryClient();
  const interviews = client.getQueryData([
    "interviews",
    { groupID: base?.team?.groupID }
  ]) as any[];

  return (
    <Stack direction={"row"}>
      {/* {status === "failed:cloudRecordingAvailable" && (
        <Tooltip title="Retry">
          <IconButton>
            <IconRetry />
          </IconButton>
        </Tooltip>
      )} */}

      {interviewId && (
        <Tooltip title="Redirect to this interview">
          <IconButton
            disabled={status !== "successful" && interviewId === null}
            onClick={() => {
              selectInterview(setBase, interviewId, interviews);
              navigate(`/discovery`);
            }}
            onMouseEnter={() => setRedirectionHover(true)}
            onMouseLeave={() => setRedirectionHover(false)}
          >
            <IconRedirect style={{ color: redirectionHover ? theme.custom.mainButtonHighlight : "#828282", cursor: "pointer" }}/>
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

const meetingListTableColumns = [
  {
    accessorKey: "topic",
    header: "Event Name",
    size: 120,
    Cell: (cell) => {
      return <Typography fontSize="15px">{cell.row.original.topic}</Typography>;
    }
  },
  {
    accessorFn: (row) => moment(row.startTime).format(getDateFormat()),
    header: "Date",
    id: "date",
    size: 70,
    sortingFn: (cellA, cellB) => {
      return (
        moment(cellA.original.startTime).valueOf() -
        moment(cellB.original.startTime).valueOf()
      );
    },
    Cell: (cell) => {
      return (
        <Box>
          <Typography fontSize="15px">
            {moment(cell.row.original.startTime).format(getDateFormat())}
          </Typography>
          <Typography fontSize="15px">
            {moment(cell.row.original.startTime).format("LT")}
          </Typography>
        </Box>
      );
    }
  },
  {
    accessorKey: "recordingStatus",
    header: "Status",
    size: 60,
    Cell: (cell) => {
      const status: ZoomRecordingStatus = cell.row.original.recordingStatus;
      const IconComponent = STATUS_ICON_MAP[status] || null;

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "row"
          }}
        >
          <Tooltip title={STATUS_TEXT_MAP[status] || ""}>
            {status === "meetingRecording" ? (
              <Box
                component="div"
                sx={(theme: Theme) => ({
                  ...RECORDING_INDICATOR_STYLES(theme),
                  width: 16,
                  height: 16
                })}
              ></Box>
            ) : (
              <Box sx={{ color: "#828282" }}>
                {IconComponent ? <IconComponent /> : null}
              </Box>
            )}
          </Tooltip>
        </Box>
      );
    }
  },
  {
    accessorKey: "hostUUID",
    header: "",
    size: 10,
    id: "action",
    enableSorting: false,
    Cell: (cell) => <MeetingListAction meeting={cell.row.original} />
  }
] as MRT_ColumnDef<TMeetingResponse>[];

interface MeetingListProps {
  zoomMeetings: TMeetingResponse[];
  isMeetingLoading: boolean;
  isMeetingRefetching: boolean;
  meetingRefetch: () => Promise<QueryObserverResult<TMeetingResponse[], Error>>;
}

export default function MeetingList({
  zoomMeetings,
  isMeetingLoading,
  isMeetingRefetching,
  meetingRefetch,
}: MeetingListProps) {
  const theme = useTheme();
  const [zoomIntegration, setZoomIntegration] = useAtom(
    zoomIntegrationStateAtom
  );
  const columns = useMemo<MRT_ColumnDef<TMeetingResponse>[]>(() => {
    return meetingListTableColumns;
  }, []);

  useEffect(() => {
    async function refetchingMeetingList() {
      if (zoomIntegration.meetingListRefetching) {
        await meetingRefetch();
        setZoomIntegration((prev) => ({
          ...prev,
          meetingListRefetching: false
        }));
      }
    }

    refetchingMeetingList();
  }, [zoomIntegration.meetingListRefetching]);

  if (
    isMeetingLoading ||
    (zoomIntegration.meetingListRefetching && isMeetingRefetching)
  ) {
    return <LinearProgress />;
  }
  
  if(!isMeetingLoading && !isMeetingRefetching && !zoomMeetings?.length){
    return (
      <Box sx={{height:"100%", width:'100%',display: "flex",
        flexDirection: "column",
        justifyContent: "center", 
        alignItems: "center",}}>
        <IconMeetingList/>
        <Typography fontSize="15px" align="center" sx={{color:theme.custom.mainButtonHighlight, m:'-45px 0 0 -12px'}}>
          No meeting found!
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "white",
        pt: 2,
        borderRadius: 1,
        boxShadow: 1
      }}
    >
      <GlobalTable
        columns={columns}
        data={zoomMeetings}
        config={{
          initialState: {
            showGlobalFilter: true,
            sorting: [
              {
                id: "date",
                desc: false
              }
            ]
          },
          muiTableBodyRowProps: () => ({
            hover: false,
            sx: {
              cursor: "pointer",
              backgroundColor: "transparent",
              "&:hover .MuiIconButton-root": {
                opacity: 1,
                pointerEvents: "auto"
              },
              ":hover": { backgroundColor: theme.custom.hoverOptionColor }
            }
          }),
          muiTableBodyCellProps: ({ column }) => ({
            sx: {
              height: "auto !important",
              py: "6px",
              ...(column.id === "action" && {
                "& .MuiIconButton-root": {
                  opacity: 0,
                  pointerEvents: "none",
                  transition: "opacity 0.2s ease-in-out"
                }
              })
            }
          }),
          renderEmptyRowsFallback: ({ table }) => (
            <Typography fontSize="15px" align="center" mt={2}>
              No meeting found!
            </Typography>
          )
        }}
      />
    </Box>
  );
}
